import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { collection, addDoc, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { COLLECTIONS } from '../../firebase/collections';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import { NotificationService } from '../../services/NotificationService';

const WorkRequestComments = ({ workRequest, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const commentsEndRef = useRef(null);

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError('');

      if (!workRequest.id) {
        setError('ID de demande manquant');
        setLoading(false);
        return;
      }

      // Vérifier si la collection WORK_REQUEST_COMMENTS existe dans COLLECTIONS
      if (!COLLECTIONS.WORK_REQUEST_COMMENTS) {
        console.error('La collection WORK_REQUEST_COMMENTS n\'est pas définie');
        setComments([]);
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, COLLECTIONS.WORK_REQUEST_COMMENTS),
          where('workRequestId', '==', workRequest.id),
          orderBy('createdAt', 'asc')
        );

        const querySnapshot = await getDocs(q);
        const commentsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || null
        }));

        setComments(commentsData);
      } catch (queryError) {
        // Si l'erreur est liée à un chemin vide, on affiche un message spécifique
        if (queryError.message && queryError.message.includes('empty path')) {
          console.error('Erreur de chemin vide:', queryError);
          setComments([]);
        } else {
          throw queryError; // Propager l'erreur pour être gérée par le catch externe
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
      setError('Erreur lors du chargement des commentaires');
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [workRequest.id]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;
    
    try {
      setSubmitting(true);
      setError('');

      if (!currentUser || !workRequest.id) {
        setError('Informations utilisateur ou demande manquantes');
        setSubmitting(false);
        return;
      }

      const commentData = {
        workRequestId: workRequest.id,
        text: newComment.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || 'Utilisateur',
        createdAt: serverTimestamp(),
        isInternal: false // Visible par tous
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.WORK_REQUEST_COMMENTS), commentData);
      
      // Créer une notification pour le nouveau commentaire
      await NotificationService.createCommentNotification(
        workRequest, 
        newComment.trim(), 
        currentUser.uid
      );

      setNewComment('');
      
      // Ajouter le nouveau commentaire à la liste locale
      const newCommentWithId = {
        id: docRef.id,
        ...commentData,
        createdAt: new Date()
      };
      
      setComments(prevComments => [...prevComments, newCommentWithId]);
      
      if (onCommentAdded) {
        onCommentAdded(newCommentWithId);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error);
      setError('Erreur lors de l\'envoi du commentaire');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarColor = (userId) => {
    if (!userId) return '#757575';
    
    // Générer une couleur basée sur l'ID utilisateur
    const hash = userId.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 60%)`;
  };

  const isCurrentUserComment = (comment) => {
    return comment.createdBy === currentUser?.uid;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Commentaires et suivi
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 2, 
          maxHeight: '400px', 
          overflowY: 'auto',
          bgcolor: '#f9f9f9'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Aucun commentaire pour le moment
          </Typography>
        ) : (
          comments.map((comment) => (
            <Box 
              key={comment.id} 
              sx={{ 
                mb: 2,
                display: 'flex',
                flexDirection: isCurrentUserComment(comment) ? 'row-reverse' : 'row',
                alignItems: 'flex-start'
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: getAvatarColor(comment.createdBy),
                  width: 36, 
                  height: 36,
                  mr: isCurrentUserComment(comment) ? 0 : 1,
                  ml: isCurrentUserComment(comment) ? 1 : 0
                }}
              >
                {getInitials(comment.createdByName)}
              </Avatar>
              <Box 
                sx={{ 
                  maxWidth: '80%',
                  bgcolor: isCurrentUserComment(comment) ? '#e3f2fd' : 'white',
                  borderRadius: 2,
                  p: 1.5,
                  boxShadow: 1
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2" color="text.primary">
                    {comment.createdByName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {comment.createdAt ? format(comment.createdAt, 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Date inconnue'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {comment.text}
                </Typography>
              </Box>
            </Box>
          ))
        )}
        <div ref={commentsEndRef} />
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            placeholder="Ajouter un commentaire..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={submitting}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Box>
              <Tooltip title="Joindre un fichier">
                <IconButton color="primary" disabled={submitting}>
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Ajouter une image">
                <IconButton color="primary" disabled={submitting}>
                  <ImageIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              disabled={!newComment.trim() || submitting}
              type="submit"
            >
              {submitting ? <CircularProgress size={24} /> : 'Envoyer'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default WorkRequestComments;
